import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input, OnChanges,
  OnInit, OnDestroy,
  Output, SimpleChanges,
  ViewChild, HostListener
} from '@angular/core';
import { QuillEditorComponent } from 'ngx-quill';
import { UsersService, MessageBoardService } from '@app/core';
import { ActivatedRoute } from '@angular/router';
import { Cookie } from 'ng2-cookies/ng2-cookies';
import Quill from 'quill';
import Toolbar from 'quill/modules/toolbar';
//import "quill-mention";
import MagicUrl from 'quill-magic-url';
Quill.register('modules/magicUrl', MagicUrl);

const Font = Quill.import('formats/font') as any;
Font.whitelist = [
  'roboto',
  'montserrat',
  'bangers',
  'birthstone',
  'pacifico',
  'caveat',
  'jura',
  'limelight',
  'oregano',
  'handlee',
  'kranky',
  'eater',
  'creepster',
  'nosifer',
  'ranchers',
  'chicle',
  'unkempt',
  'moc',
];
Quill.register(Font, true);

import { bindings } from './modules/keyboard';
import { embedVideos } from './modules/clipboard';
import { link } from './modules/link';
import CustomVideoBlot from '@app/shared/comments/ss-editor/modules/video-blot';
import Mention from './modules/quill-mention/quill.mention';
import QuoteBlot from './modules/quote-blot';
import { Popup } from '../../../shared/comments/ss-editor/modules/popup';
import {
  EMOJI_DATA,
  EMOJI_CATEGORIES,
  EMOJI_KEYWORDS,
  getCategoryEmojis,
  EMOTICON_MAP
} from '../../emoji';;



@Component({
  selector: 'app-ss-editor',
  templateUrl: './ss-editor.component.html',
  styleUrls: ['./ss-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SsEditorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() editorContent: any = '';
  @Input() placeholder: any = 'Add a comment...';
  @Input() autofocus = false;
  @Input() state = 'close';
  @Input() showPostButton = true;
  @Input() showSendSetting = false;
  @Input() showSetting = true;
  @Input() hideToolbar = false;
  @Input() fontSize = true;
  @Output() editorContentChange = new EventEmitter<string>();
  @Output() editorFocus = new EventEmitter<string>();
  @Output() editorBlur = new EventEmitter<string>();
  @Output() onCommentPost: EventEmitter<any> = new EventEmitter();
  //Cancel Comment
  @Output() onCancelCommentPost: EventEmitter<any> = new EventEmitter();
  //@Output() ('onCancelCommentEdit') onCancelCommentEdit: EventEmitter<any> = new EventEmitter();
  @ViewChild('editor', { static: true }) editor: QuillEditorComponent;
  @ViewChild('modal', { static: false }) private OkModalComponent;

  @ViewChild('emojiPopup') emojiPopup!: ElementRef;


  // This runs on any click in the document
  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // If popup is closed, do nothing
    if (!this.showEmojiPopup) return;

    const target = event.target as HTMLElement;

    const clickedInsidePopup = this.emojiPopup?.nativeElement.contains(target);

    // 👇 Locate your emoji button (the one that toggles popup)
    const emojiButton = document.getElementById('emojiButton');

    const clickedEmojiButton = emojiButton?.contains(target);

    // If click is inside popup OR on the emoji button, DO NOTHING
    if (clickedInsidePopup || clickedEmojiButton) {
      return;
    }

    // Otherwise → close popup
    this.showEmojiPopup = false;
  }

  allEmojis = EMOJI_DATA;
  categories = EMOJI_CATEGORIES;
  emojiKeywords = EMOJI_KEYWORDS;
  emoticonMap = EMOTICON_MAP;
  activeCategory = 'smileys';
  filteredEmojis: string[] = [];
  searchTerm = '';
  recentEmojis = [];
  searchMode = false;


  enterSend = false;
  modules = {};
  uploadingImage = false;
  imageWidth = 700;
  private popupMovedToBody = false;
  showEmojiPopup = false;

  // Default popup position
  popupPosition: any = {
    top: '200px',
    left: '200px',
    position: 'fixed',
    zIndex: 999999
  };

  quill!: Quill;
  editorCreated = false;
  savedSelection: ReturnType<Quill['getSelection']> | null = null;
  displayHtmlLength = 0;
  displayTextLength = 0;
  constructor(private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private userService: UsersService,
    private route: ActivatedRoute,
    private messageBoardService: MessageBoardService) {


    Quill.register({
      'formats/quote': QuoteBlot,
      'modules/mention': Mention,
      //     'formats/emoji': EmojiBlot,
      CustomVideoBlot,

    }, true);
  }

  // Stored so it can be removed on destroy — this component mounts often
  // (comments/messages/boards); inline listeners would accumulate on document.
  dragPreventHandler = (e: Event) => {
    if (!(e.target as HTMLElement).closest('.ql-editor')) {
      e.preventDefault();
    }
  };

  ngOnDestroy() {
    document.removeEventListener('dragover', this.dragPreventHandler);
    document.removeEventListener('drop', this.dragPreventHandler);
  }

  ngOnInit() {

    document.addEventListener('dragover', this.dragPreventHandler);
    document.addEventListener('drop', this.dragPreventHandler);

    this.enterSend = Cookie.get('enterSend') === 'true';

    this.modules = {
      clipboard: {
        matchVisual: false,
      },
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        source: (searchTerm, renderList, mentionChar) => {
          this.userService.getUsers(searchTerm).subscribe(({ data }) => {
            renderList(data.map(user => {
              return {
                id: user.id,
                value: user.screen_name,
                email: user.email,
                newsletter: user.newsletter
              };
            }));
          });
        },
      },
      toolbar: {
        container: true,
      },
      keyboard: { bindings },
      magicUrl: true,
    };

    const savedCategory = localStorage.getItem('emojiCategory');
    this.activeCategory = savedCategory || 'smileys';
    this.filteredEmojis = getCategoryEmojis(this.activeCategory);
  }

  mapFontSize(fontSize: string): string | null {
    const px = parseFloat(fontSize);
    if (isNaN(px)) return null;

    if (px <= 12)  return 'small';
    if (px <= 17)  return null;   // normal — no attribute
    if (px <= 26)  return 'large';
    return 'huge';                // 27px+
  }

  readImageWidth(file) {
    let img = document.createElement('img');
    let blob = URL.createObjectURL(file);
    img.src = blob;
    img.id = 'hiddenImage';
    img.onload = () => {
      this.imageWidth = img.width;
      console.log(this.imageWidth)
      URL.revokeObjectURL(blob)
    }
  }

  setCategory(cat: string) {
    this.activeCategory = cat;
    this.searchTerm = '';
    this.filteredEmojis = this.allEmojis[cat] || [];
  }

  changeCategory(name: string) {
    this.activeCategory = name;
    localStorage.setItem('emojiCategory', name);
    this.filteredEmojis = getCategoryEmojis(name);
    this.searchTerm = '';
  }

  filterEmojis() {
    const term = this.searchTerm.trim().toLowerCase();

    if (!term) {
      // empty search → show all
      this.filteredEmojis = Object.values(this.allEmojis).flat();
      return;
    }

    // global search across ALL emojis
    this.filteredEmojis = Object.keys(this.emojiKeywords).filter(emoji =>
      this.emojiKeywords[emoji]?.some(keyword =>
        keyword.includes(term)
      )
    );
  }

  insertEmoji(e: string, event?: MouseEvent) {

    if (!this.editor.quillEditor) return;

    // SHIFT-CLICK = multi insert (NO pop-up movement)
    if (event?.shiftKey) {
      event.preventDefault();  // <-- KEY FIX
      event.stopPropagation(); // <-- KEY FIX

      const range = this.editor.quillEditor.getSelection(true);
      this.editor.quillEditor.insertText(range.index, e);
      this.editor.quillEditor.setSelection(range.index + e.length);

      return; // <-- do not close, do not re-render, do not move popup
    }

    // NORMAL CLICK = close popup and insert
    const range = this.editor.quillEditor.getSelection(true);
    this.editor.quillEditor.insertText(range.index, e);
    this.editor.quillEditor.setSelection(range.index + e.length);

    this.showEmojiPopup = false;
  }

  openSearchMode() {
    this.searchMode = true;

    // show ALL emojis to start search
    this.filteredEmojis = Object.values(this.allEmojis).flat();
    this.searchTerm = '';

    setTimeout(() => {
      const input = document.querySelector('.emoji-search') as HTMLInputElement;
      input?.focus();
    }, 0);
  }

  closeSearchMode() {
    this.searchMode = false;
    this.searchTerm = '';

    // return to active category view
    this.filteredEmojis = getCategoryEmojis(this.activeCategory);

    // optional: re-focus popup to prevent jitter
    this.cdr.markForCheck();
  }

  checkForEmoticonReplace(delta) {
    const quill = this.editor.quillEditor;

    if (!delta || !delta.ops) return;

    // Detect inserted character
    const inserted = delta.ops.find(op => typeof op.insert === 'string');
    if (!inserted) return;

    const insertedChar = inserted.insert;

    // Slack triggers replacement only when typing punctuation/space/newline
    const triggerChars = [' ', '\n', '.', ',', '!', '?', ':', ';', ')', ']', '}'];

    // If the typed character is NOT a trigger, do nothing
    if (!triggerChars.includes(insertedChar)) return;

    // Get cursor position AFTER insertion
    const range = quill.getSelection();
    if (!range) return;

    const cursorIndex = range.index;

    // Maximum emoticon length = 5 chars
    const lookback = 5;
    const start = Math.max(0, cursorIndex - lookback);

    // Extract previous characters (remove ending whitespace/newlines)
    const lastChars = quill
      .getText(start, lookback)
      .replace(/\s+$/, "");

    // Try to match emoticons
    for (const emo of Object.keys(this.emoticonMap)) {

      if (lastChars.endsWith(emo)) {

        const emoji = this.emoticonMap[emo];
        const emoLength = emo.length;

        // Delete emoticon text (NOT the trigger character)
        quill.deleteText(cursorIndex - emoLength - 1, emoLength, 'user');

        // Insert emoji
        quill.insertText(cursorIndex - emoLength - 1, emoji, 'user');

        // Cursor should remain after emoji + trigger
        quill.setSelection(cursorIndex - emoLength - 1 + emoji.length, 0);

        return;
      }
    }
  }

  onQuillKeydownEmoji(e: KeyboardEvent) {
    const quill = this.editor.quillEditor;

    const triggerChars = [' ', '\n', '.', ',', '!', '?', ':', ';', ')', ']', '}'];

    // Only convert *after* these
    if (!triggerChars.includes(e.key)) return;

    const range = quill.getSelection();
    if (!range) return;

    const cursorIndex = range.index;

    // 👇 1) DO NOT run emoticon logic inside or immediately after a mention
    const leaf = quill.getLeaf(cursorIndex)[0];

    if (leaf && leaf.parent && leaf.parent.domNode?.classList.contains('ql-mention')) {
      // Cursor is inside the mention blot → skip
      return;
    }

    const prevLeaf = quill.getLeaf(cursorIndex - 1)?.[0];
    if (prevLeaf && prevLeaf.parent && prevLeaf.parent.domNode?.classList.contains('ql-mention')) {
      // Typing immediately AFTER a mention @tony :) → skip
      return;
    }

    // 👇 2) Gather previous characters for emoticon detection
    const lookback = 5;
    const start = Math.max(0, cursorIndex - lookback);
    const raw = quill.getText(start, lookback);
    const lastChars = raw.replace(/\s+$/, "");

    // 👇 3) Try matching emoticons
    for (const emo of Object.keys(this.emoticonMap)) {
      if (lastChars.endsWith(emo)) {

        // Prevent Quill from inserting trigger char before replacement
        e.preventDefault();

        const emoji = this.emoticonMap[emo];
        const emoLen = emo.length;

        // Replace emoticon
        quill.deleteText(cursorIndex - emoLen, emoLen, 'user');
        quill.insertText(cursorIndex - emoLen, emoji + e.key, 'user');

        // Move cursor correctly
        quill.setSelection(cursorIndex - emoLen + emoji.length + 1, 0);

        return;
      }
    }
  }

  onCategoryClick(name: string) {
    this.searchMode = false;          // leave search mode
    this.searchTerm = '';             // clear search input
    this.activeCategory = name;       // set highlight
    this.filteredEmojis = getCategoryEmojis(name);

    localStorage.setItem('emojiCategory', name);
  }


  addToRecent(e: string) {
    let rec = this.recentEmojis.filter(x => x !== e);
    rec.unshift(e);
    this.recentEmojis = rec.slice(0, 20);
    localStorage.setItem('recentEmojis', JSON.stringify(this.recentEmojis));
  }

  toggleEmojiPopup() {
    this.showEmojiPopup = !this.showEmojiPopup;

    if (!this.showEmojiPopup) return;

    if (!this.popupMovedToBody && this.emojiPopup?.nativeElement) {
      const popupEl = this.emojiPopup.nativeElement;

      popupEl.style.position = 'fixed';
      document.body.appendChild(popupEl);

      this.popupMovedToBody = true;
    }

    this.setCategory(this.activeCategory);

    setTimeout(() => {
      const quill = this.editor?.quillEditor;
      const range = quill?.getSelection();

      if (range && range.index !== null) {
        // Cursor exists → position at caret
        this.positionPopupAtCursor();
      } else {
        // No cursor → place popup above the emoji button
        this.positionEmojiPopup();
      }
    }, 0);
  }

  positionPopupAtCursor() {
    if (!this.editor?.quillEditor) return;

    const quill = this.editor.quillEditor;
    const range = quill.getSelection();
    if (!range) return;

    const bounds = quill.getBounds(range.index);

    const wrapper = document.querySelector('.editor-wrapper') as HTMLElement | null;
    const qlEditor = wrapper?.querySelector('.ql-editor') as HTMLElement | null;

    if (!wrapper || !qlEditor) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const editorRect = qlEditor.getBoundingClientRect();

    const caretX = bounds.left + (editorRect.left - wrapperRect.left);
    const caretY = bounds.top + (editorRect.top - wrapperRect.top);

    const popupWidth = 372;
    const popupHeight = 436;

    // Normal placement: above caret
    let top = caretY - popupHeight - 10;
    top -= 50;  // your desired “float above caret” offset

    let left = caretX;

    // --- FIX: Prevent top cutoff ---
    if (top < 0) {
      // move popup down just a bit so it's fully visible
      top = 10;

      // ✨ SHIFT RIGHT by one character width (requested behavior)
      left += bounds.width || 22; // fallback width if bounds.width is 0
    }

    // --- clamp bottom ---
    if (top + popupHeight > wrapper.clientHeight) {
      top = wrapper.clientHeight - popupHeight + 100;
    }

    // --- Horizontal checks ---
    if (left + popupWidth > wrapper.clientWidth - 10) {
      left = wrapper.clientWidth - popupWidth - 10;
    }
    if (left < 0) left = 0;

    this.popupPosition = {
      top: `${top}px`,
      left: `${left}px`,
      position: "absolute",
      zIndex: 999999
    };

    this.cdr.markForCheck();
  }


  positionEmojiPopup() {
    const wrapper = document.querySelector('.editor-wrapper') as HTMLElement | null;
    if (!wrapper) return;

    const emojiButton = document.getElementById('emojiButton') as HTMLElement | null;
    if (!emojiButton) return;

    const qlContainer = wrapper.querySelector('.ql-container') as HTMLElement | null;
    if (!qlContainer) return;

    const btnRect = emojiButton.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const popupWidth = 372;
    const popupHeight = 436;
    const margin = 6;

    const scrollY = qlContainer.scrollTop;
    const scrollX = qlContainer.scrollLeft;
// alert(wrapperRect.top)
    let left = (btnRect.left - wrapperRect.left) + scrollX;
    let top = (btnRect.bottom - wrapperRect.top) + scrollY + margin;


  // --- 1. Position popup to the RIGHT of button ---
  // let left = btnRect.right + 8; // 8px spacing
  // let top = btnRect.top - (popupHeight / 2) + (btnRect.height / 2);

    if (left + popupWidth > wrapper.clientWidth) {
      left = wrapper.clientWidth - popupWidth - 10;
    }

    if (top + popupHeight > wrapper.clientHeight) {
      top = wrapper.clientHeight - popupHeight + 100;
    }

    //not required when no cursor
    //top -= 50;

    this.popupPosition = {
      top: `${top}px`,
      left: `${left}px`,
      position: "absolute",
      zIndex: 999999
    };

    this.cdr.markForCheck();
  }


  onClickOutside(event: MouseEvent) {
    if (!this.showEmojiPopup) return;

    const target = event.target as HTMLElement;

    // If clicked inside popup → ignore
    if (this.emojiPopup?.nativeElement.contains(target)) return;

    // If clicked emoji button → ignore
    const emojiButton = document.getElementById('emojiButton');
    if (emojiButton && emojiButton.contains(target)) return;

    // Otherwise → hide popup
    this.showEmojiPopup = false;
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes.editorContent && typeof changes.editorContent.currentValue === 'string' && changes.editorContent.currentValue.length === 0) {
      setTimeout(() => {
        (this.editor.quillEditor as Quill).setContents([
          {
            insert: ''
          },
          {
            insert: '\n'
          }
        ] as any);
        this.editorContent = '';
        this.cdr.detectChanges();
      }, 100);
    }
  }

  onEditorCreated(quill: Quill) {
    this.quill = quill;
    // IMPORTANT — attach handlers to the *real* toolbar Quill built
    const toolbar = quill.getModule('toolbar') as any;

    toolbar.addHandler('image', () => {
      console.log("IMAGE HANDLER REAL FIRED!");
      this.selectLocalImage();
    });

    toolbar.addHandler('link', (value: boolean) => {
      if (value) {
        const popup = new Popup();
        const range = this.quill.getSelection();
        let preview: string, linkName: string;

        if (range == null || range.length == 0) {
          popup.prompt({
            title: 'Enter your link',
            buttons: {
              okButton: 'ok',
              cancelButton: 'cancel'
            },
            inputs: {
              textInput: 'Text:',
              urlInput: 'Url:'
            }
          }, (result: { confirm: string; textInput: string; urlInput: string }) => {

            if (result.confirm === 'okButton') {
              linkName = result.textInput;
              preview = this.absoluteUrl(result.urlInput);
              this.quill.insertText(range ? range.index : 0, linkName, 'link', preview, 'user');
            }
          });
          return;
        }

        popup.prompt({
          title: 'Enter your link',
          buttons: {
            okButton: 'ok',
            cancelButton: 'cancel'
          },
          inputs: {
            urlInput: 'Url:'
          }
        }, (result: { confirm: string; urlInput: string }) => {

          if (result.confirm === 'okButton') {
            const url = this.absoluteUrl(result.urlInput || 'https://singsnap.com');
            this.quill.format('link', url);
          }
        });

      } else {
        this.quill.format('link', false);
      }
    });

    // Add your selection-change listener
    quill.on('selection-change', () => {
      if (!this.showEmojiPopup) return;

      const range = quill.getSelection();

      if (range) {
        this.positionPopupAtCursor();
      } else {
        this.positionEmojiPopup();
      }
    });

    quill.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        this.checkForEmoticonReplace(delta);
      }

      if (this.showEmojiPopup) this.positionPopupAtCursor();
    });

    const stored = localStorage.getItem('recentEmojis');
    if (stored) this.recentEmojis = JSON.parse(stored);

    quill.root.addEventListener('paste', (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const file = item.getAsFile();

        if (file && this.isImageFile(file)) {
          e.preventDefault();
          e.stopPropagation();
          this.readImageWidth(file);
          this.saveToServer(file);
          return;
        }
      }
    });


    quill.root.addEventListener('drop', (ev: DragEvent) => {
      ev.preventDefault();

      const items = ev.dataTransfer?.items;
      if (!items) return;

      // Items first
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const file = item.getAsFile();
        if (file && this.isImageFile(file)) {
          ev.preventDefault();
          this.readImageWidth(file);
          this.saveToServer(file);
          return;
        }
      }

      // Fallback for browsers that only expose files[]
      const files = ev.dataTransfer?.files;
      if (files) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (this.isImageFile(file)) {
            ev.preventDefault();
            this.readImageWidth(file);
            this.saveToServer(file);
            return;
          }
        }
      }

      // Prevent Quill inserting base64 image
      quill.blur();
      setTimeout(() => quill.focus(), 0);
    });

    // Add these two targeted matchers instead:
    const clipboard = quill.getModule('clipboard') as any;

    // Matcher for inline styles (external paste)
    clipboard.addMatcher('SPAN', (node: HTMLElement, delta: any) => {
      const ops = delta.ops.map((op: any) => {
        if (typeof op.insert !== 'string') return op;
        const attrs = { ...(op.attributes || {}) };

        // Font size from inline style
        if (node.style?.fontSize) {
          const size = this.mapFontSize(node.style.fontSize);
          if (size) attrs['size'] = size;
        }

        // Font family from inline style
        if (node.style?.fontFamily) {
          const font = node.style.fontFamily
            .split(',')[0]
            .replace(/['"]/g, '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-');
          if (Font.whitelist.includes(font)) {
            attrs['font'] = font;
          }
        }

        // Font size from ql-size-xxx class (internal Quill copy)
        const sizeClass = Array.from(node.classList).find(c => c.startsWith('ql-size-'));
        if (sizeClass) {
          attrs['size'] = sizeClass.replace('ql-size-', '');
        }

        // Font from ql-font-xxx class (internal Quill copy)
        const fontClass = Array.from(node.classList).find(c => c.startsWith('ql-font-'));
        if (fontClass) {
          const font = fontClass.replace('ql-font-', '');
          if (Font.whitelist.includes(font)) {
            attrs['font'] = font;
          }
        }

        return { ...op, attributes: attrs };
      });
      return { ops };
    });

  }


  /**
   * Step1. select local image
   *
   */
  selectLocalImage() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    // Listen upload local image and save to server
    input.onchange = () => {
      const file = input.files[0];

      // file type is only image.
      if (this.isImageFile(file)) {
        this.readImageWidth(file)
        this.saveToServer(file);
      } else {
        console.warn('You could only upload images.');
      }
    };
  }

  /**
   * Step2. save to server
   *
   * @param {File} file
   */
  saveToServer(file: File) {
    const id = this.route.snapshot.params && this.route.snapshot.params.id;
    this.messageBoardService.uploadMedia(null, file).subscribe(response => {
      this.insertToEditor(response.data);
    });
  }

  /**
   * Step3. insert image url to rich editor.
   *
   * @param {string} url
   */
  insertToEditor(url: string) {
    const quill = this.editor.quillEditor;

    // Resize if needed
    if (this.imageWidth >= 700) {
      url = url + '?w=700';
    }

    // Get safe insertion index
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();

    // Insert image embed as block element (OPTION A)
    quill.insertEmbed(index, 'image', url, 'user');

    // 🔥 FORCE QUILL TO WRITE THE DOM
    quill.update('user');

    // 🔥 TRIGGER FULL RENDER (the missing piece!)
    quill.setSelection(index + 1, 0, 'silent');
    quill.setSelection(index + 1, 0, 'user');

    // 🔥 ENSURE HTML IS NOW CORRECT
    const html = quill.root.innerHTML;

    // Sync content back to parent component
    this.editorContent = html;
    this.editorContentChange.emit(html);

    // Done
    this.uploadingImage = false;
  }


  postComment() {

    this.onCommentPost.emit(this.editorContent);
  }

  cancelPostComment() {
    //this.onCancelCommentEdit.emit(this.editorContent);
    this.onCancelCommentPost.emit(this.editorContent);
  }

  onContentChangedHandle(obj: any): void {
    const quill = this.editor.quillEditor as Quill;
    const htmlContent = quill.root.innerHTML;
    const htmlLength = htmlContent.length;
    const textLength = quill.getLength();
    //alert (htmlContent)
    if (htmlContent === "<p><br></p>") {
      this.displayHtmlLength = 0;
      this.displayTextLength = 0;
    } else {
      this.displayHtmlLength = htmlLength;
      this.displayTextLength = textLength - 1;
    }
    if (obj.text.length <= 10000) {
      this.editorContent = obj.html;
      this.editorContentChange.emit(obj.html);
    } else {
      this.editor.quillEditor.deleteText(10000, obj.text.length);
      console.log('content is too long!')
    }
    console.log(obj)
  }

  onEditorBlur(e: any): void {
    if (this.editorBlur) {
      this.editorBlur.emit(e);
    }
  }

  onEditorFocus(e: any): void {
    this.state = 'open';
    if (this.editorFocus) {
      this.editorFocus.emit(e);
    }
    this.cdr.detectChanges();
  }


    setEditContent(body: string) {
    // If Quill is not yet created, wait and retry
    if (!this.editor || !this.editor.quillEditor) {
      setTimeout(() => this.setEditContent(body), 30);
      return;
    }

    const quill = this.editor.quillEditor;

    // Safely paste HTML
    quill.clipboard.dangerouslyPasteHTML(body, 'api');

    // Move cursor to end
    quill.setSelection(quill.getLength(), 0);

    // Refresh toolbar/focus if needed
    this.onEditorBlur('e');
    this.onEditorFocus('e');
  }



  quote(quote) {
    const authorId = quote.author.id;
    const authorName = quote.author.screen_name;
    const author = `<span class="ql-quote-author" data-id="${authorId}" data-value="${authorName}">&#8212; ${authorName}</span>`;
    const body = `${quote.body} <br>${author}`;
    this.editor.quillEditor.setSelection(this.editor.quillEditor.getLength());

    this.editor.quillEditor.updateContents([
      {
        retain: this.editor.quillEditor.getLength()
      },
      {
        insert: '\n'
      },
      {
        attributes: {
          quoteContent: body
        },
        insert: {
          quote: body
        }
      },
      {
        insert: '\n'
      }
    ] as any);
    this.editor.quillEditor.focus();
    this.editor.quillEditor.setSelection(this.editor.quillEditor.getLength() + 1 as any);
  }

  focus() {
    this.editor.quillEditor.focus();
    this.cdr.detectChanges();
  }

  onQuillKeyDown(e) {
    if (e.keyCode === 13 && this.enterSend && this.editorContent) {
      this.postComment();
    }
  }

  onEnterSend(value) {
    Cookie.set('enterSend', value + '');
  }


  restoreCursorInQuill() {
    if (this.savedSelection) {
      const quill = this.editor.quillEditor as Quill;
      quill.focus();

      // Ensure Quill has time to focus before restoring selection
      setTimeout(() => {
        quill.setSelection(this.savedSelection!.index, this.savedSelection!.length);
      }, 0);
    }
  }

  /** Global file detector: fixes WebP, GIF, missing MIME types, OS drag issues */
  isImageFile(file: File): boolean {
    if (!file) return false;

    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();

    return (
      type.startsWith("image/") ||
      name.endsWith(".png") ||
      name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".gif") ||
      name.endsWith(".webp")
    );
  }

  absoluteUrl(url: string): string {
      if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url)) {
          return 'https://' + url;
      }
      return url;
  }


}
